import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { Typography, Box, IconButton, useMediaQuery, useTheme } from '@mui/material'; // Importar useMediaQuery y useTheme
import MenuIcon from '@mui/icons-material/Menu'; // Icono de hamburguesa

import Login from './pages/Login';
import UserDashboard from './pages/UserDashboard';
import PlantaDashboard from './pages/PlantaDashboard';
import FabricanteDashboard from './pages/FabricanteDashboard';
import RutinasDashboard from './pages/RutinaDashboard';
import RutinadDetail from './pages/RutinaDetail';
import TipoRutinaDetail from './pages/TipoRutinaDetail';
import TipoRutinaDashboard from './pages/TipoRutinaDashboard';
import ResponderRutinas from './pages/ResponderRutinas';
import ReporteRutinas from './pages/ReportesRutinas';

import Sidebar from './pages/Sidebar';
import HomeDashboard from './pages/HomeDashboard';
import HomeUser from './pages/HomeUser';

import './styles.css'; // Asegúrate de que este archivo exista

// Definir el ancho del drawer aquí también o importarlo si está en un archivo de constantes
const drawerWidth = 0; // Debe coincidir con el ancho en Sidebar.js

// Componente para la protección de rutas basado en roles (Recomendado)
const ProtectedRoute = ({ allowedRoles, role, isAuthenticated, redirectPath = "/login" }) => {
  if (!isAuthenticated) {
    return <Navigate to={redirectPath} replace />;
  }
  if (allowedRoles && !allowedRoles.includes(role)) {
    // Si el usuario no tiene el rol permitido, redirige a una página de acceso denegado o home
    // Puedes cambiar '/home' por una ruta específica de acceso denegado
    return <Navigate to={role === 'admin' ? '/home' : '/home_usr'} replace />;
  }
  return <Outlet />; // Renderiza las rutas anidadas
};


function App() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // true si es pantalla pequeña

  // --- Estado de autenticación y rol (persistente al recargar) ---
  const [auth, setAuth] = useState(() => {
    const storedAuth = localStorage.getItem('isAuthenticated');
    return storedAuth === 'true'; // Convertir string a booleano
  });
  const [role, setRole] = useState(() => localStorage.getItem('userRole'));

  // --- Estado de la Sidebar (depende de si es móvil) ---
  const [isSidebarOpen, setIsSidebarOpen] = useState(!isMobile);

  // Sincronizar isSidebarOpen con el tamaño de la pantalla
  useEffect(() => {
    setIsSidebarOpen(!isMobile);
  }, [isMobile]);

  // Persistir estado de autenticación y rol en localStorage
  useEffect(() => {
    if (auth) {
      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('userRole', role);
    } else {
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('userRole');
      setRole(null); // Limpiar el rol al desloguearse
    }
  }, [auth, role]);


  const toggleSidebar = () => setIsSidebarOpen(prev => !prev);

  const ProtectedLayout = () => {
    // Si no está autenticado, redirige al login
    if (!auth) return <Navigate to="/" replace />;

    return (
      // La clase `app-container` y `sidebar-closed` son de tu CSS
      <Box sx={{ display: 'flex', minHeight: '100vh' }}>

        {/* Botón de hamburguesa visible solo si es móvil Y la sidebar está cerrada */}
        {!isSidebarOpen && (
          <IconButton
            className="sidebar-toggle-button hamburger-menu" // Revisa tu CSS para estas clases
            onClick={toggleSidebar}
            sx={{
              position: 'fixed', // Posiciona el botón de forma fija
              top: 16,
              left: 16,
              zIndex: theme.zIndex.drawer + 1, // Asegura que esté por encima del drawer
              color: 'text.primary', // Color del icono
              backgroundColor: 'background.paper', // Fondo para que resalte
              borderRadius: '50%',
              p: 1,
              boxShadow: 2,
            }}
          >
            <MenuIcon />
          </IconButton>
        )}

        <Sidebar
          isOpen={isSidebarOpen}
          toggleSidebar={toggleSidebar}
          setAuth={setAuth} // Pasa setAuth para cerrar sesión
          role={role}
        />



        <div className="main-content"
          style={{
            marginLeft: isMobile ? 0 : (isSidebarOpen ? drawerWidth : 0),
            transition: theme.transitions.create('margin', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          }}
        >
          {/* El contenido de las rutas anidadas */}
          <Outlet />
        </div>
      </Box>
    );
  };

  return (
    <Router>
      <Routes>
        {/* Ruta de Login: Si NO está autenticado, muestra el Login; si SÍ, redirige al home según el rol */}
        <Route
          path="/"
          element={
            !auth ? (
              <Login setAuth={setAuth} setRole={setRole} />
            ) : (
              <Navigate
                  to={
                    role === 'admin' || role === 'supervisor' || role === 'coordinador'
                      ? '/home'
                      : '/home_usr'
                  }
                  replace
                />
            )
          }
        />

        {/* Rutas Protegidas bajo ProtectedLayout */}
        <Route element={<ProtectedLayout />}>

          {/* Rutas para todos los roles autenticados */}
          {/* Mueve la ruta de "rutinas" aquí */}
          <Route path="rutinas" element={<RutinasDashboard />} />
          <Route path="rutina_detail/:id" element={<RutinadDetail />} />


          {/* home para  'admin' */}
          <Route element={<ProtectedRoute allowedRoles={['admin', 'coordinador', 'supervisor']} role={role} isAuthenticated={auth} />}>
            <Route path="/home" element={<HomeDashboard />} />
            
          </Route>

          {/* Rutas accesibles solo para 'admin' */}
          <Route element={<ProtectedRoute allowedRoles={['admin']} role={role} isAuthenticated={auth} />}>
            
            <Route path="plantas" element={<PlantaDashboard />} />
            <Route path="usuarios" element={<UserDashboard />} />
            <Route path="fabricantes" element={<FabricanteDashboard />} />
            <Route path="tiporutinas" element={<TipoRutinaDashboard />} />
            <Route path="tipo_rutina_detail/:id" element={<TipoRutinaDetail />} />
            <Route path="rutina_detail/:id" element={<RutinadDetail />} />
            <Route path="reportes" element={<ReporteRutinas />} />
          </Route>

          {/* Rutas accesibles para 'operator', 'coordinador', 'supervisor', 'tecnico' (no admin) */}
          {/* Asegúrate de que los roles aquí coincidan con lo que esperas */}
          <Route element={<ProtectedRoute allowedRoles={['operator', 'coordinador', 'supervisor', 'tecnico']} role={role} isAuthenticated={auth} />}>
            <Route path="home_usr" element={<HomeUser />} />
            {/* La ruta para responder rutinas también debe ser accesible para todos los roles que pueden verlas */}
            <Route path="responder_rutina/:id" element={<ResponderRutinas />} />

          </Route>

          {/* Ruta Catch-all para páginas no encontradas dentro del layout protegido */}
          <Route path="*" element={<Typography variant="h5" sx={{ p: 4 }}>Página no encontrada</Typography>} />

        </Route>
      </Routes>
    </Router>
  );
}

export default App;